import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '@/hooks/use-auth-wrapper'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { Loader2, User, Phone, Calendar, Shield, Save } from 'lucide-react'
import { getInitials, formatPhone } from '@/lib/utils'
import { toast } from 'sonner'

const perfilSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  telefone: z.string().optional(),
})

type PerfilFormData = z.infer<typeof perfilSchema>

export default function PerfilPage() {
  const { perfil, isLoading, updatePerfil, isUpdatingPerfil } = useAuth()
  const [isEditing, setIsEditing] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<PerfilFormData>({
    resolver: zodResolver(perfilSchema),
    defaultValues: {
      nome: perfil?.nome || '',
      telefone: perfil?.telefone || '',
    },
  })

  const telefoneValue = watch('telefone')

  React.useEffect(() => {
    if (perfil) {
      reset({
        nome: perfil?.nome || '',
        telefone: perfil?.telefone || '',
      })
    }
  }, [perfil, reset])

  const handleTelefoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(event.target.value)
    setValue('telefone', formatted, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
  }

  const onSubmit = async (data: PerfilFormData) => {
    try {
      await updatePerfil(data)
      toast.success('Perfil atualizado com sucesso!')
      setIsEditing(false)
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error)
      toast.error('Erro ao atualizar perfil')
    }
  }

  const handleCancel = () => {
    reset()
    setIsEditing(false)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (!perfil) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">Perfil não encontrado</h3>
              <p className="text-muted-foreground">
                Não foi possível carregar os dados do seu perfil.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Meu Perfil</h1>
            <p className="text-muted-foreground">
              Gerencie suas informações pessoais e configurações
            </p>
          </div>
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)}>
              <User className="h-4 w-4 mr-2" />
              Editar Perfil
            </Button>
          )}
        </div>
      </motion.div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Informações Básicas */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="md:col-span-1"
        >
          <Card>
            <CardHeader className="text-center">
              <Avatar className="h-20 w-20 mx-auto">
                <AvatarFallback className="text-lg">
                  {getInitials(perfil.nome)}
                </AvatarFallback>
              </Avatar>
              <CardTitle>{perfil.nome}</CardTitle>
              <CardDescription>Perfil do usuário</CardDescription>
              <Badge variant="secondary" className="w-fit mx-auto">
                <Shield className="h-3 w-3 mr-1" />
                {perfil.permissao ? perfil.permissao.charAt(0).toUpperCase() + perfil.permissao.slice(1) : ''}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Membro desde {perfil.criado_em ? new Date(perfil.criado_em).toLocaleDateString('pt-BR') : 'Data não disponível'}
              </div>
              {perfil.permissao && (
                <div className="flex items-center gap-2 text-sm">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <Badge variant="secondary">
                    {perfil.permissao ? perfil.permissao.charAt(0).toUpperCase() + perfil.permissao.slice(1) : ''}
                  </Badge>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <div className={`h-2 w-2 rounded-full bg-green-500`} />
                Ativo
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Formulário de Edição */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="md:col-span-2"
        >
          <Card>
            <CardHeader>
              <CardTitle>Informações Pessoais</CardTitle>
              <CardDescription>
                {isEditing
                  ? 'Atualize suas informações pessoais'
                  : 'Suas informações pessoais cadastradas'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="nome"
                        {...register('nome')}
                        disabled={!isEditing}
                        className="pl-10"
                        placeholder="Seu nome"
                      />
                    </div>
                    {errors.nome && (
                      <p className="text-sm text-destructive">{errors.nome.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="telefone">Telefone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="telefone"
                        {...register('telefone')}
                        value={telefoneValue || ''}
                        onChange={handleTelefoneChange}
                        disabled={!isEditing}
                        className="pl-10"
                        placeholder="(11) 99999-9999"
                      />
                    </div>
                    {errors.telefone && (
                      <p className="text-sm text-destructive">{errors.telefone.message}</p>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <>
                    <Separator />
                    <div className="flex gap-2 justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isUpdatingPerfil}
                      >
                        Cancelar
                      </Button>
                      <Button type="submit" disabled={isUpdatingPerfil}>
                        {isUpdatingPerfil ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Salvar Alterações
                      </Button>
                    </div>
                  </>
                )}
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}